import React from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Divider, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import MagnifyPlusOutlineIcon from 'mdi-react/MagnifyPlusOutlineIcon';
import MagnifyMinusOutlineIcon from 'mdi-react/MagnifyMinusOutlineIcon';
import FitToPageOutlineIcon from 'mdi-react/FitToPageOutlineIcon';
import { editorTheme, floatingSlabSx } from '../utils/editor-theme';

interface Props {
  /** 1 is fit-to-view. Shown as a percentage so zooming has a state, not just a feel. */
  readonly zoom: number;
  readonly onZoomIn: () => void;
  readonly onZoomOut: () => void;
  readonly onFitToView: () => void;
}

/**
 * Zoom, on the drawing rather than in a bar at the bottom of the screen.
 *
 * It belongs where the eye already is. Zoom is adjusted constantly while drawing — in and out
 * of a corner, back out to see the whole floor — and a control at the far edge of the window
 * costs a trip across the screen every time.
 */
const EditorZoomBar = ({ zoom, onZoomIn, onZoomOut, onFitToView }: Props): React.JSX.Element => {
  const { t } = useTranslation();

  const button = (
    labelKey: string,
    Icon: typeof MagnifyPlusOutlineIcon,
    onClick: () => void,
  ): React.JSX.Element => (
    <Tooltip title={String(t(labelKey))}>
      <IconButton size="small" onClick={onClick} aria-label={String(t(labelKey))} sx={{ color: editorTheme.inkDim }}>
        <Icon size={17} />
      </IconButton>
    </Tooltip>
  );

  return (
    <Box
      sx={{ ...floatingSlabSx, left: 12, bottom: 12 }}
    >
      <Stack direction="row" alignItems="center" sx={{ px: 0.5 }}>
        {button('floorplan-editor-zoom-out', MagnifyMinusOutlineIcon, onZoomOut)}

        {/* Tabular figures, so the number does not jitter left and right as it changes — which
            is exactly what it does while somebody is zooming. */}
        <Typography
          variant="caption"
          sx={{
            minWidth: 46,
            textAlign: 'center',
            color: editorTheme.inkDim,
            fontFamily: editorTheme.numeric,
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {`${Math.round(zoom * 100)}%`}
        </Typography>

        {button('floorplan-editor-zoom-in', MagnifyPlusOutlineIcon, onZoomIn)}
        <Divider orientation="vertical" flexItem sx={{ mx: 0.25, my: 0.75, borderColor: editorTheme.lineSoft }} />
        {button('floorplan-editor-fit-to-view', FitToPageOutlineIcon, onFitToView)}
      </Stack>
    </Box>
  );
};

export default EditorZoomBar;
